import { computed, type Ref } from "vue";
import type { Workflow, Service } from "@/types/workflow-types";
import type { ConnectionTemplateDetail } from "@/types";
import type { ScopeNode } from "@/types/bbel-types";
import type { ServiceOutputSample } from "@/queries/output-samples";
import { headerShapeNodes, outputShapeNodes } from "@/views/builder/components/bbel/bbel-scope-output";

/**
 * Client-side scope tree for the universal BBEL editor (#5683), derived from the workflow the builder
 * already holds. It drives autocomplete and cheap advisory squiggles; the publish-time validator
 * (server-side) remains the authority, so a lag here only costs a suggestion, never a wrong rejection.
 *
 * The fixed-scope members below mirror the engine's scope bindings
 * (JintExpressionEngine.Flatten* / WorkflowScopeSchema) and are kept in sync by hand. org.* is
 * camelCase (matching system.* / response.*), per #5669.
 */
export const ORG_MEMBERS = ["id", "name", "domain", "region", "timezone", "locale"];
export const SYSTEM_MEMBERS = ["now", "requestId", "callbackUrl", "webhookUrl"];
export const RESPONSE_MEMBERS = ["body", "headers", "statusCode", "query"];

/** Projections each service type publishes (ADR-020 §2) — mirrors the server's per-model
 *  `PublishedProjections` (ServiceProjectionSchema) so the picker only offers what a reference to
 *  that service can actually take. All result-side by design: the request-as-sent is a redacted
 *  diagnostics artifact (#6010), never expression-addressable. Offered for autocomplete but left
 *  open (a service node isn't `closed`), so this list lagging the server only costs a suggestion. */
const PROJECTIONS_BY_TYPE: Record<string, string[]> = {
  http: ["output", "raw", "headers", "statusCode"],
  httpPaging: ["output", "raw", "headers", "statusCode"],
  // No statusCode: a webhook's answer arrives as an inbound POST, which has no status line.
  webhook: ["output", "raw", "headers"],
  sftp: ["output", "raw"],
  split: ["output", "iteration"],
  javaScript: ["output"],
  expression: ["output"],
  batch: ["output", "iteration", "stats"],
  flatten: ["output", "iteration", "stats"],
  group: ["output", "iteration", "stats"],
  join: ["output", "stats"],
  filter: ["output", "stats"],
  concat: ["output", "raw", "stats"],
  rejoin: ["output", "stats"],
  // No output: a Wait carries no data.
  wait: ["stats"],
  aggregate: ["output", "stats"],
  subWorkflow: ["output", "stats"],
  return: ["output", "stats"],
};
const DEFAULT_PROJECTIONS = ["output"];

const PROJECTION_HINTS: Record<string, string> = {
  output: "Parsed result",
  raw: "Raw response body",
  headers: "Response headers",
  statusCode: "HTTP status code",
  iteration: "Position in the fan-out: index, total, isFirst, isLast",
  stats: "Transaction counts of the service",
};

export function leaf(parent: string, segment: string): ScopeNode {
  return { segment, path: `${parent}.${segment}`, label: segment };
}

/** A fixed engine scope: its members are the complete, known set → closed. */
export function fixedScope(name: string, members: string[]): ScopeNode {
  return { segment: name, path: name, label: name, closed: true, children: members.map((m) => leaf(name, m)) };
}

/** A runtime-bound scope: a known root (so `name.*` doesn't squiggle) with no closed member set. */
export function openScope(name: string): ScopeNode {
  return { segment: name, path: name, label: name };
}

/** A service's projection node, enriched from the project's latest sample where one exists —
 *  `output` grows the sampled body shape, `headers` the sampled header names, `statusCode` a value
 *  hint. All still OPEN: a sample is one run's shape, not a contract, so validation strictness is
 *  unchanged. A truncated sample says so in the output hint. */
function projectionNode(serviceId: string, projection: string, sample: ServiceOutputSample | undefined): ScopeNode {
  const node: ScopeNode = { ...leaf(`services.${serviceId}`, projection), hint: PROJECTION_HINTS[projection] };
  if (!sample) return node;

  switch (projection) {
    case "output": {
      const children = outputShapeNodes(sample.sampleJson, node.path);
      if (children.length > 0) node.children = children;
      if (sample.truncated) node.hint = `${PROJECTION_HINTS.output} · sample truncated — shape may be incomplete`;
      break;
    }
    case "headers": {
      const children = headerShapeNodes(sample.headers ?? {}, node.path);
      if (children.length > 0) node.children = children;
      break;
    }
    case "statusCode":
      if (sample.statusCode != null) node.hint = `${PROJECTION_HINTS.statusCode} · ${sample.statusCode}`;
      break;
  }
  return node;
}

/** Transitive descendants of a service — every service that reaches back to it through a chain of
 *  `parentServiceIds`. Selecting any of these (or the service itself) as a parent would close a
 *  loop, so the relationship editor excludes this set from its candidate list. */
export function descendantServiceIds(services: readonly Service[], currentServiceId: string): Set<string> {
  const childrenById = new Map<string, string[]>();
  for (const service of services) {
    for (const parentId of service.parentServiceIds ?? []) {
      const children = childrenById.get(parentId) ?? [];
      children.push(service.id);
      childrenById.set(parentId, children);
    }
  }

  const descendants = new Set<string>();
  const stack = [...(childrenById.get(currentServiceId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (descendants.has(id)) continue;
    descendants.add(id);
    for (const childId of childrenById.get(id) ?? []) stack.push(childId);
  }
  return descendants;
}

/** Transitive ancestors of a service — the services whose output it can read at run time, walked
 *  through `parentServiceIds`. A service can only reference upstream data, never a sibling or a
 *  downstream service (which hasn't run), so the scope is filtered to this set. */
function ancestorServiceIds(services: readonly Service[], currentServiceId: string): Set<string> {
  const parentsById = new Map(services.map((s) => [s.id, s.parentServiceIds ?? []]));
  const ancestors = new Set<string>();
  const stack = [...(parentsById.get(currentServiceId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (ancestors.has(id)) continue;
    ancestors.add(id);
    for (const parent of parentsById.get(id) ?? []) stack.push(parent);
  }
  return ancestors;
}

/** Build the workflow-context scope roots for the BBEL editor. Pure — testable without a component.
 *  When `currentServiceId` is given, the `services` root is filtered to that service's ancestors
 *  (upstream-only); omit it to expose every service. */
export function buildWorkflowScope(
  workflow: Workflow | null | undefined,
  outputSamples?: Record<string, ServiceOutputSample>,
  slotTemplates?: Record<string, ConnectionTemplateDetail>,
  currentServiceId?: string,
): ScopeNode[] {
  if (!workflow) return [];

  // services.<id>.<projection> — service ids are the complete set (closed); the projection layer
  // is offered but open (type-dependent), so an unknown member below a service isn't flagged.
  // The segment is the service ID, not the display name: the runtime services dict and the
  // transaction-source links resolve by id only (SubstitutionMaterialiser), so a name-authored
  // reference fails at run time. The label carries pipeline position + human name for browsing,
  // like Make's module list. Position numbering is the original workflow order, preserved across
  // the ancestor filter.
  const allServices = workflow.services ?? [];
  const ancestors =
    currentServiceId !== undefined ? ancestorServiceIds(allServices, currentServiceId) : undefined;
  const services: ScopeNode = {
    segment: "services",
    path: "services",
    label: "services",
    closed: true,
    children: allServices
      .map((s, i) => ({ service: s, position: i }))
      .filter(({ service }) => service.id && (!ancestors || ancestors.has(service.id)))
      .map(({ service: s, position }) => ({
        segment: s.id,
        path: `services.${s.id}`,
        label: `${position + 1}. ${s.name || s.id}`,
        children: (PROJECTIONS_BY_TYPE[s.serviceModelType?.$type ?? ""] ?? DEFAULT_PROJECTIONS).map(
          (p) => projectionNode(s.id, p, outputSamples?.[s.id]),
        ),
      })),
  };

  // wizard.<fieldId> — the vueform schema field names across every step (mirrors the server's
  // WizardStepDefaults.GetFieldNames). Complete set → closed.
  const wizardFields = new Set<string>();
  for (const def of workflow.wizardDefinitions ?? [])
    for (const step of def.steps ?? [])
      for (const field of Object.keys(step.properties?.vueformConfig?.schema ?? {}))
        wizardFields.add(field);
  const wizard: ScopeNode = {
    segment: "wizard",
    path: "wizard",
    label: "wizard",
    closed: true,
    children: [...wizardFields].map((f) => leaf("wizard", f)),
  };

  // connection.<slot>.<field> — slot names are the complete set (closed). Below a slot, the
  // template's declared fields (union across its auth methods) are offered; the node stays OPEN
  // because the runtime object can carry more (auth-time onResponse writes). Secret fields show
  // for discoverability but LOCKED — they're never materialised into expressions (#6010), so the
  // linter flags any use (server publish gate to follow, #6012).
  const connection: ScopeNode = {
    segment: "connection",
    path: "connection",
    label: "connection",
    closed: true,
    children: (workflow.connectionSlots ?? [])
      .filter((slot) => slot.name)
      .map((slot) => {
        const methods = slotTemplates?.[slot.connectionTemplateId]?.authMethods ?? [];
        const fields = new Map<string, boolean>();
        for (const m of methods)
          for (const f of m.fields) fields.set(f.name, (fields.get(f.name) ?? true) && f.secret);
        const node: ScopeNode = { segment: slot.name, path: `connection.${slot.name}`, label: slot.name };
        if (fields.size > 0)
          node.children = [...fields.entries()].map(([name, secret]) => ({
            ...leaf(`connection.${slot.name}`, name),
            ...(secret ? { locked: true, hint: "Secret — never available in expressions" } : {}),
          }));
        return node;
      }),
  };

  return [
    services,
    wizard,
    connection,
    fixedScope("org", ORG_MEMBERS),
    fixedScope("system", SYSTEM_MEMBERS),
    fixedScope("response", RESPONSE_MEMBERS),
    // Runtime-bound shapes: known scopes, members open (advisory only).
    openScope("platform"),
    openScope("flow"),
    openScope("inbound"),
    openScope("credentials"),
    openScope("datastore"),
    // Per-run inputs ({{ input.<key> }}): permissive like datastore — the reference IS the
    // declaration, discovered by GetWorkflowInputKeys and collected in the Run / Saved Call modals.
    // Mirrors WorkflowScopeSchema's server-side treatment of "input".
    openScope("input"),
  ];
}

/** Reactive workflow-context scope roots for the BBEL editor. `currentServiceId` scopes the
 *  `services` root to the edited service's ancestors (upstream-only); omit for the full set. */
export function useBbelScope(
  workflow: Ref<Workflow | null | undefined>,
  outputSamples?: Ref<Record<string, ServiceOutputSample>>,
  slotTemplates?: Ref<Record<string, ConnectionTemplateDetail>>,
  currentServiceId?: Ref<string | null | undefined>,
) {
  return computed<ScopeNode[]>(() =>
    buildWorkflowScope(
      workflow.value,
      outputSamples?.value,
      slotTemplates?.value,
      currentServiceId?.value ?? undefined,
    ),
  );
}
